/**
 * Web UI App 鈥?thin wrapper that:
 * 1. Fetches WS config from the server
 * 2. Verifies the publishing access token and sets window.electronAPI
 * 3. Delegates to the Electron renderer's App component
 *
 * Mobile responsiveness is handled by container queries and isAutoCompact
 * in the shared renderer components 鈥?no webui-specific layout hacks needed.
 */

import React, { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { getLocale, setLocale, t } from '@okclawpro/i18n'
import type { Locale } from '@okclawpro/i18n'
import { createWebApi } from './adapter/web-api'
import type { WsRpcClient } from '../../electron/src/transport/client'
import { connectPublicAccessPeerWithFallback, resolvePublicAccessLink } from './public-access'
import RemoteEntryApp from './remote/RemoteEntryApp'
import { isHeadlessUpdateInProgress } from '../../electron/src/renderer/lib/headless-update-state'
import {
  buildAccessTokenHeaders,
  clearStoredAccessToken,
  enterVerifiedWebApp,
  resolveAccessTokenState,
  shouldClearStoredAccessToken,
  shouldAutoInitializeAccessToken,
  storeAccessToken,
} from './access-token'

// Lazy-load the Electron App after window.electronAPI is set up.
// This prevents any Electron component from accessing window.electronAPI
// before the web adapter is ready.
const ElectronApp = lazy(() => import('@/App'))

type Phase = 'token' | 'loading' | 'ready'

const WEBUI_FALLBACK_LOCALE: Locale = 'zh-CN'
const UPDATE_RECONNECT_RETRY_MS = 2500

interface InitializeOptions {
  keepReadyDuringReconnect?: boolean
  retryWhileUpdating?: boolean
}

function isPrivateHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase()
  if (normalized === 'localhost' || normalized === '127.0.0.1' || normalized === '::1' || normalized === '[::1]') return true
  if (normalized.endsWith('.local') || normalized.endsWith('.lan')) return true
  if (!normalized.includes('.')) return true
  if (normalized.startsWith('192.168.')) return true
  if (normalized.startsWith('10.')) return true
  const private172 = normalized.match(/^172\.(\d{1,2})\./)
  if (private172) {
    const second = Number(private172[1])
    return second >= 16 && second <= 31
  }
  return false
}

export function isPublicGatewayRoot(href = window.location.href): boolean {
  const publicLink = resolvePublicAccessLink(href)
  if (publicLink) return false
  const url = new URL(href)
  return url.pathname === '/' && url.protocol === 'https:' && !isPrivateHostname(url.hostname)
}

function normalizeLocale(locale: unknown): Locale | null {
  if (locale === 'en' || locale === 'zh-CN') return locale
  return null
}

async function applyDesktopLocale(locale: unknown): Promise<void> {
  const normalized = normalizeLocale(locale)
  if (!normalized || normalized === getLocale()) return
  await setLocale(normalized)
}

function LoadingScreen({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-screen font-sans text-foreground/50 gap-3">
      <div className="animate-spin w-6 h-6 border-2 border-current border-t-transparent rounded-full" />
      <p className="text-[13px]">{label ?? t('webui.loading.connecting')}</p>
    </div>
  )
}

function AccessTokenScreen({
  initialToken,
  error,
  isSubmitting,
  locale,
  onSubmit,
}: {
  initialToken: string
  error: string
  isSubmitting: boolean
  locale: Locale
  onSubmit: (accessToken: string) => void
}) {
  const [accessToken, setAccessToken] = useState(initialToken)
  void locale

  useEffect(() => {
    setAccessToken(initialToken)
  }, [initialToken])

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-5 py-8 font-sans text-foreground">
      <form
        className="w-full max-w-[440px] overflow-hidden rounded-lg border border-border/70 bg-background shadow-[0_18px_60px_rgba(15,23,42,0.10)]"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit(accessToken)
        }}
      >
        <div className="border-b border-border/70 px-6 pb-5 pt-6">
          <div className="mb-4 flex h-10 w-auto min-w-10 items-center justify-center rounded-lg bg-primary px-2 text-sm font-semibold text-primary-foreground">
            OK Work
          </div>
          <p className="text-[12px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
            {t('webui.accessToken.eyebrow')}
          </p>
          <h1 className="mt-2 text-[22px] font-semibold leading-7 text-foreground">
            {t('webui.accessToken.title')}
          </h1>
          <p className="mt-2 text-[13px] leading-5 text-muted-foreground">
            {t('webui.accessToken.description')}
          </p>
        </div>
        <div className="flex flex-col gap-4 px-6 pb-6 pt-5">
          <label className="flex flex-col gap-2 text-[13px] font-medium text-foreground">
            {t('webui.accessToken.inputLabel')}
            <input
              className="h-11 rounded-md border border-border bg-background px-3 font-mono text-[13px] outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/15"
              value={accessToken}
              onChange={(event) => setAccessToken(event.target.value)}
              placeholder={t('webui.accessToken.placeholder')}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          {error ? (
            <p className="rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-[13px] leading-5 text-destructive">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={isSubmitting || accessToken.trim().length === 0}
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-zinc-950 px-4 text-[14px] font-semibold text-white shadow-[0_10px_22px_rgba(15,23,42,0.18)] transition-all hover:bg-zinc-800 active:translate-y-px active:shadow-[0_6px_14px_rgba(15,23,42,0.14)] disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:text-zinc-500 disabled:shadow-none dark:bg-zinc-50 dark:text-zinc-950 dark:hover:bg-zinc-200 dark:disabled:bg-zinc-700 dark:disabled:text-zinc-400"
          >
            {isSubmitting ? (
              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : null}
            {isSubmitting ? t('webui.accessToken.connecting') : t('webui.accessToken.submit')}
          </button>
          <p className="rounded-md border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-[12px] leading-5 text-amber-700 dark:text-amber-300">
            {t('webui.accessToken.securityNote')}
          </p>
        </div>
      </form>
    </div>
  )
}

export default function App() {
  if (isPublicGatewayRoot()) {
    return <RemoteEntryApp />
  }

  return <LegacyWebUiApp />
}

function LegacyWebUiApp() {
  const [phase, setPhase] = useState<Phase>('token')
  const [initialAccessTokenState] = useState(() => resolveAccessTokenState(window.location.href))
  const initialAccessToken = initialAccessTokenState.token
  const [error, setError] = useState('')
  const [isSubmittingToken, setIsSubmittingToken] = useState(false)
  const [locale, setLocaleState] = useState<Locale>(() => getLocale())
  const [localeReady, setLocaleReady] = useState(() => getLocale() === WEBUI_FALLBACK_LOCALE)
  const clientRef = useRef<WsRpcClient | null>(null)
  const publicConnectionRef = useRef<Awaited<ReturnType<typeof connectPublicAccessPeerWithFallback>> | null>(null)
  const autoRestoreStartedRef = useRef(false)

  const syncLocale = async (nextLocale: unknown) => {
    await applyDesktopLocale(nextLocale)
    setLocaleState(getLocale())
  }

  const formatAccessTokenError = (err: unknown): string => {
    const message = err instanceof Error ? err.message : String(err)
    if (message.trim().toLowerCase() === 'invalid token') {
      return t('webui.accessToken.invalid')
    }
    if (message.includes('Desktop device is offline')) {
      return t('webui.accessToken.publicDesktopOffline')
    }
    if (message.includes('Public access signaling')) {
      return t('webui.accessToken.publicSignalingFailed')
    }
    if (message.includes('Public peer-to-peer connection timeout')) {
      return t('webui.accessToken.publicP2pTimeout')
    }
    if (message.includes('Public peer-to-peer connection')) {
      return t('webui.accessToken.publicP2pFailed')
    }
    if (message.includes('Connection timeout') || message.includes('timeout after')) {
      return t('webui.accessToken.connectionTimeout')
    }
    if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
      return t('webui.accessToken.connectionFailed')
    }
    if (!/[\u4e00-\u9fff]/.test(message)) {
      return t('webui.accessToken.connectionFailed')
    }
    return message
  }

  const closePublicConnection = () => {
    if (!publicConnectionRef.current) return
    publicConnectionRef.current.peer.close()
    publicConnectionRef.current.signaling.close()
    publicConnectionRef.current = null
  }

  const handlePublicAccessDisconnected = () => {
    if (isHeadlessUpdateInProgress()) {
      closePublicConnection()
      clientRef.current?.destroy()
      clientRef.current = null
      setError('')
      setPhase('ready')
      setIsSubmittingToken(false)
      void window.electronAPI.reconnectTransport?.().catch(() => undefined)
      window.setTimeout(() => {
        if (!isHeadlessUpdateInProgress()) return
        void initialize(initialAccessToken, {
          keepReadyDuringReconnect: true,
          retryWhileUpdating: true,
        })
      }, UPDATE_RECONNECT_RETRY_MS)
      return
    }

    closePublicConnection()
    clientRef.current?.destroy()
    clientRef.current = null
    ;(window as any).electronAPI = undefined
    setError(t('webui.accessToken.disconnectedByDesktop'))
    setPhase('token')
    setIsSubmittingToken(false)
  }

  async function initialize(accessToken: string, options: InitializeOptions = {}) {
    const trimmedAccessToken = accessToken.trim()
    if (!trimmedAccessToken) {
      setError(t('webui.accessToken.required'))
      setPhase('token')
      return
    }

    if (!options.keepReadyDuringReconnect) {
      setPhase('loading')
    }
    setError('')
    setIsSubmittingToken(true)

    try {
      // 1. Fetch WS URL from the server.
      const publicLink = resolvePublicAccessLink(window.location.href)
      if (publicLink) {
        if (!('RTCPeerConnection' in window)) {
          throw new Error(t('webui.accessToken.p2pUnsupported'))
        }

        const publicConnection = await connectPublicAccessPeerWithFallback(publicLink, {
          onDisconnected: handlePublicAccessDisconnected,
          onPairing: (pairing) => syncLocale(pairing.locale),
        })
        try {
          const { pairing, socket } = publicConnection

          if (clientRef.current) {
            clientRef.current.destroy()
          }

          const { api, client } = createWebApi({
            serverUrl: 'datachannel://public-access',
            workspaceId: pairing.workspaceId,
            accessToken: trimmedAccessToken,
            reconnectStorageKey: `okclaw-public-access:${pairing.deviceCode}`,
            createSocket: () => socket as unknown as WebSocket,
          })
          clientRef.current = client
          publicConnectionRef.current = publicConnection
          enterVerifiedWebApp({
            api,
            installApi: (installedApi) => { (window as any).electronAPI = installedApi },
            connectTransport: () => client.connect(),
            persistAccess: () => storeAccessToken(trimmedAccessToken),
            enterReady: () => setPhase('ready'),
          })
          return
        } catch (error) {
          publicConnection.peer.close()
          publicConnection.signaling.close()
          if (publicConnectionRef.current === publicConnection) {
            publicConnectionRef.current = null
          }
          throw error
        }
      }

      const configRes = await fetch('/api/config', {
        credentials: 'same-origin',
        headers: buildAccessTokenHeaders(accessToken),
      })
      if (!configRes.ok) {
        const status = configRes.status
        if (status === 401) {
          throw new Error(t('webui.accessToken.invalid'))
        }
        throw new Error(t('webui.accessToken.configFailed', { status: String(status) }))
      }

      const { wsUrl, locale } = await configRes.json() as { wsUrl: string, locale?: string }
      await syncLocale(locale)
      if (!wsUrl) throw new Error(t('webui.accessToken.noWsUrl'))

      // 2. Determine workspace 鈥?check URL params first
      const params = new URLSearchParams(window.location.search)
      let workspaceId = params.get('workspace') ?? undefined

      // If no workspace in URL, fetch the default from the server
      // so we can include it in the WebSocket handshake
      if (!workspaceId) {
        try {
          const wsRes = await fetch('/api/config/workspaces', {
            credentials: 'same-origin',
            headers: buildAccessTokenHeaders(accessToken),
          })
          if (wsRes.ok) {
            const { defaultWorkspaceId } = await wsRes.json() as { defaultWorkspaceId?: string }
            if (defaultWorkspaceId) workspaceId = defaultWorkspaceId
          }
        } catch {
          // Non-fatal 鈥?workspace will be set via switchWorkspace later
        }
      }

      // 3. Create web API adapter
      // Destroy previous client on retry
      if (clientRef.current) {
        clientRef.current.destroy()
      }

      const { api, client } = createWebApi({
        serverUrl: wsUrl,
        workspaceId,
        accessToken: trimmedAccessToken,
      })
      clientRef.current = client

      // 4. Set window.electronAPI 鈥?must happen before any Electron component mounts
      ;(window as any).electronAPI = api

      // 5. Connect the WebSocket client
      client.connect()

      storeAccessToken(trimmedAccessToken)
      setPhase('ready')
    } catch (err) {
      if (options.retryWhileUpdating && isHeadlessUpdateInProgress()) {
        setError('')
        setPhase('ready')
        setIsSubmittingToken(false)
        window.setTimeout(() => {
          if (!isHeadlessUpdateInProgress()) return
          void initialize(accessToken, options)
        }, UPDATE_RECONNECT_RETRY_MS)
        return
      }

      const msg = formatAccessTokenError(err)
      setError(msg)
      setPhase('token')
      if (shouldClearStoredAccessToken(err, t('webui.accessToken.invalid'))) {
        clearStoredAccessToken()
      }
      clientRef.current?.destroy()
      clientRef.current = null
      closePublicConnection()
      ;(window as any).electronAPI = undefined
    } finally {
      setIsSubmittingToken(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await applyDesktopLocale(WEBUI_FALLBACK_LOCALE)
      } finally {
        if (!cancelled) {
          setLocaleState(getLocale())
          setLocaleReady(true)
        }
      }
      if (cancelled) return

      try {
        const response = await fetch('/api/config/locale', { credentials: 'same-origin' })
        if (!response.ok) return
        const { locale } = await response.json() as { locale?: string }
        if (!cancelled) {
          await syncLocale(locale)
        }
      } catch {
        // Best effort: the access-token gate falls back to Chinese until the desktop locale is available.
      }
    })()

    return () => {
      cancelled = true
      // Cleanup on unmount
      clientRef.current?.destroy()
      closePublicConnection()
    }
  }, [])

  useEffect(() => {
    if (autoRestoreStartedRef.current) return
    if (!localeReady) return
    if (!shouldAutoInitializeAccessToken(window.location.href, initialAccessTokenState)) return

    autoRestoreStartedRef.current = true
    void initialize(initialAccessToken)
  }, [initialAccessToken, initialAccessTokenState, localeReady])

  const handleAccessTokenSubmit = (accessToken: string) => {
    void initialize(accessToken)
  }

  if (!localeReady) return <LoadingScreen label="正在加载..." />

  if (phase === 'token') {
    return (
      <AccessTokenScreen
        initialToken={initialAccessToken}
        error={error}
        isSubmitting={isSubmittingToken}
        locale={locale}
        onSubmit={handleAccessTokenSubmit}
      />
    )
  }
  if (phase === 'loading') return <LoadingScreen />

  return (
    <Suspense fallback={<LoadingScreen />}>
      <ElectronApp />
    </Suspense>
  )
}
